import { z } from 'zod'

/**
 * Why a booking was let go. Mirrors the `CancellationReason` enum in the Prisma
 * schema — the two must stay in sync.
 *
 * A list rather than free text, so losses are countable by cause: "how many did
 * we lose to weather last year" is a question a venue asks, and it cannot be
 * answered by reading notes one at a time. An optional note carries the rest.
 *
 * - `client_cancelled` — the client called it off.
 * - `weather` — conditions made it impossible.
 * - `venue_issue` — something on our side: damage, a double-booking, closure.
 * - `duplicate` — the same event exists twice; this is the copy being retired.
 * - `other` — with a note, for the tail.
 * - `honeybook` — HoneyBook archived the booked project it manages; set by the
 *   sync only, never chosen by staff (`STAFF_CANCELLATION_REASONS`).
 *
 * Both the reason and the note live on the **history row**, not on the event.
 * The event only knows it is canceled; the reason belongs to the *act* of
 * canceling, and an event canceled, restored and canceled again has two of
 * them. See `docs/event-scheduling.md`.
 */
export const CANCELLATION_REASONS = [
  'client_cancelled',
  'weather',
  'venue_issue',
  'duplicate',
  'other',
  'honeybook',
] as const

export const cancellationReasonSchema = z.enum(CANCELLATION_REASONS)

/** The reasons someone canceling a booking chooses from. */
export const STAFF_CANCELLATION_REASONS = ['client_cancelled', 'weather', 'venue_issue', 'duplicate', 'other'] as const satisfies readonly (typeof CANCELLATION_REASONS)[number][]

export const staffCancellationReasonSchema = z.enum(STAFF_CANCELLATION_REASONS)

export type CancellationReason = z.infer<typeof cancellationReasonSchema>

/** How each reason reads on screen, so the form and any report agree. */
export const CANCELLATION_REASON_LABELS: Readonly<Record<CancellationReason, string>> = {
  client_cancelled: 'Client canceled',
  weather: 'Weather',
  venue_issue: 'Venue issue',
  duplicate: 'Duplicate event',
  other: 'Other',
  honeybook: 'Canceled in HoneyBook',
}
